/**
 * @fileoverview Componente responsável por agrupar os campos de configuração de leads.
 */

import React, { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import { Trash2 as Trash, ChevronDown, Copy, KeyRound } from "lucide-react";
import ConfirmationModal from "../ConfirmationModal";
import ApiTokenModal from "../ApiTokenModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  card: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.6) 0%, rgba(22, 22, 24, 0.6) 100%)'
      : '#FFFFFF',
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: "16px",
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 16px rgba(0, 0, 0, 0.3)'
      : '0 2px 12px rgba(0, 0, 0, 0.06)',
    transition: "all 0.2s ease",
    "&:hover": {
      transform: 'translateY(-2px)',
      boxShadow: theme.palette.type === 'dark'
        ? '0 8px 24px rgba(91, 124, 255, 0.2)'
        : '0 4px 20px rgba(61, 92, 253, 0.12)',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  iconButton: {
    padding: theme.spacing(1),
    borderRadius: '10px',
  },
  deleteButton: {
    color: theme.palette.type === 'dark' ? '#fca5a5' : '#ef4444',
  },
  tokenName: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
  },
  tokenHint: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
    marginTop: 4,
  },
  tokenDate: {
    fontSize: '0.75rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
    marginTop: 4,
  },
  webhookInfo: {
    padding: theme.spacing(2),
    borderRadius: '12px',
    background: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.1)'
      : 'rgba(61, 92, 253, 0.05)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.2)'
      : '1px solid rgba(61, 92, 253, 0.1)',
    marginBottom: theme.spacing(2),
  },
  webhookTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
  },
  webhookUrl: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    padding: theme.spacing(1),
    borderRadius: '8px',
    background: theme.palette.type === 'dark'
      ? 'rgba(0, 0, 0, 0.3)'
      : 'rgba(0, 0, 0, 0.05)',
    wordBreak: 'break-all',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#1e293b',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(1),
    opacity: 0.5,
  },
  // Documentação API
  apiDocAccordion: {
    background: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)',
    borderRadius: '12px !important',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    marginBottom: theme.spacing(1.5),
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: theme.spacing(0, 0, 1.5, 0) },
  },
  apiDocTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  apiDocMethod: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: '4px',
    marginRight: theme.spacing(1),
  },
  methodPost: {
    background: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
  },
  codeBlock: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    lineHeight: 1.6,
    padding: theme.spacing(2),
    borderRadius: '10px',
    background: theme.palette.type === 'dark'
      ? 'rgba(0, 0, 0, 0.4)'
      : '#f8fafc',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid #e2e8f0',
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#334155',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowX: 'auto',
    position: 'relative',
  },
  copyCodeBtn: {
    position: 'absolute',
    top: 4,
    right: 4,
    padding: 4,
    opacity: 0.6,
    '&:hover': { opacity: 1 },
  },
  fieldTypeChip: {
    fontSize: '0.7rem',
    height: 22,
    margin: 2,
  },
}));

const LeadSettingsContent = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [tokens, setTokens] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingToken, setDeletingToken] = useState(null);
  const [lastCreatedToken, setLastCreatedToken] = useState(null);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || '{API_URL}';

  const fetchTokens = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/api-tokens");
      setTokens(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStatuses = useCallback(async () => {
    try {
      const { data } = await api.get("/lead-statuses");
      setStatuses(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
    fetchStatuses();
  }, [fetchTokens, fetchStatuses]);

  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);
    socket.on(`company-${user.companyId}-lead-status`, (data) => {
      if (data.action === "create" || data.action === "update") fetchStatuses();
      if (data.action === "delete") setStatuses(prev => prev.filter(s => s.id !== data.statusId));
    });
    return () => { socket.disconnect(); };
  }, [socketManager, user.companyId, fetchStatuses]);

  const handleDeleteToken = async () => {
    if (!deletingToken) return;
    try {
      await api.delete(`/api-tokens/${deletingToken.id}`);
      toast.success(i18n.t("leadSettings.tokens.deleted"));
      fetchTokens();
    } catch (err) {
      toastError(err);
    }
    setDeletingToken(null);
    setConfirmModalOpen(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };

  const getConfirmContent = () => {
    if (deletingToken) return {
      title: i18n.t("leadSettings.tokens.deleteTitle"),
      message: i18n.t("leadSettings.tokens.deleteMessage").replace("{name}", deletingToken.name),
      onConfirm: handleDeleteToken,
    };
    return { title: "", message: "", onConfirm: () => {} };
  };
  const cc = getConfirmContent();

  const CodeBlock = ({ children }) => (
    <div style={{ position: 'relative' }}>
      <pre className={classes.codeBlock}>{children}</pre>
      <IconButton className={classes.copyCodeBtn} size="small" onClick={() => handleCopy(children)}>
        <Copy size={14} />
      </IconButton>
    </div>
  );

  const firstStatusId = statuses.length > 0 ? statuses[0].id : 1;

  const curlJsonExample = `curl -X POST ${baseUrl}/webhook/leads \\
  -H "Content-Type: application/json" \\
  -H "X-API-Token: stl_seu_token_aqui" \\
  -d '{
  "name": "Nome do cliente",
  "phone": "(11) 99999-9999",
  "email": "cliente@email.com",
  "statusId": ${firstStatusId},
  "priority": "normal",
  "source": "wordpress",
  "customFields": {
    "fields": [
      { "id": "field-1", "label": "WhatsApp", "type": "whatsapp", "value": "(11) 99999-9999", "showOnCard": true },
      { "id": "field-2", "label": "Valor", "type": "currency", "value": 5000, "showOnCard": true }
    ]
  }
}'`;

  const curlMultipartExample = `curl -X POST ${baseUrl}/webhook/leads \\
  -H "X-API-Token: stl_seu_token_aqui" \\
  -F "name=Nome do cliente" \\
  -F "phone=(11) 99999-9999" \\
  -F "email=cliente@email.com" \\
  -F "statusId=${firstStatusId}" \\
  -F "priority=normal" \\
  -F "source=wordpress" \\
  -F "files=@/caminho/para/imagem.jpg" \\
  -F "files=@/caminho/para/documento.pdf"`;

  const fieldTypes = [
    { type: "text", desc: "Texto simples" },
    { type: "whatsapp", desc: "Numero WhatsApp (clicavel no card)" },
    { type: "email", desc: "E-mail" },
    { type: "cpf", desc: "CPF com mascara" },
    { type: "currency", desc: "Valor monetario (R$)" },
    { type: "textarea", desc: "Texto longo" },
    { type: "select", desc: "Lista de opcoes" },
    { type: "checklist", desc: "Lista de tarefas com progresso" },
  ];

  return (
    <>
      <ConfirmationModal
        title={cc.title}
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setDeletingToken(null); }}
        onConfirm={cc.onConfirm}
      >{cc.message}</ConfirmationModal>

      <ApiTokenModal open={tokenModalOpen} onClose={(t) => { setTokenModalOpen(false); if (t) { setLastCreatedToken(t); fetchTokens(); } }} />

      {/* TOKENS */}
      <div className={classes.section}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography className={classes.sectionTitle}>
            <KeyRound size={18} /> Tokens de API
          </Typography>
          <Button variant="contained" color="primary" size="small" onClick={() => setTokenModalOpen(true)}>
            {i18n.t("leadSettings.tokens.newToken")}
          </Button>
        </Box>

        <div className={classes.webhookInfo}>
          <Typography className={classes.webhookTitle}>Autenticacao</Typography>
          <Typography variant="body2" style={{ marginBottom: 8, opacity: 0.8 }}>
            Todas as requisicoes devem incluir o header <code>X-API-Token</code> com seu token:
          </Typography>
          {lastCreatedToken ? (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography className={classes.webhookUrl} style={{ flex: 1 }}>
                {lastCreatedToken}
              </Typography>
              <Tooltip title="Copiar token">
                <IconButton size="small" onClick={() => handleCopy(lastCreatedToken)}>
                  <Copy size={18} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : tokens.length > 0 ? (
            <Typography variant="body2" style={{ opacity: 0.6 }}>
              O token so e exibido no momento da criacao. Crie um novo token para copiar.
            </Typography>
          ) : (
            <Typography variant="body2" style={{ opacity: 0.5 }}>
              Crie um token acima para comecar a usar a API.
            </Typography>
          )}
        </div>

        {tokens.length === 0 ? (
          <div className={classes.emptyState}>
            <KeyRound className={classes.emptyIcon} />
            <Typography variant="body1">{i18n.t("leadSettings.tokens.emptyTitle")}</Typography>
            <Typography variant="body2">{i18n.t("leadSettings.tokens.emptyDescription")}</Typography>
          </div>
        ) : (
          <div className={classes.cardsContainer}>
            {tokens.map((token) => (
              <Card key={token.id} className={classes.card}>
                <CardContent>
                  <div className={classes.cardHeader}>
                    <div>
                      <Typography className={classes.tokenName}>{token.name}</Typography>
                      <Typography className={classes.tokenHint}>{token.tokenHint}</Typography>
                      <Typography className={classes.tokenDate}>
                        Criado em {new Date(token.createdAt).toLocaleDateString()}
                      </Typography>
                    </div>
                    <IconButton size="small" className={`${classes.iconButton} ${classes.deleteButton}`}
                      onClick={() => { setDeletingToken(token); setConfirmModalOpen(true); }}>
                      <Trash size={18} strokeWidth={1.5} color="#c72b40" />
                    </IconButton>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* DOCUMENTACAO DA API */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Documentacao da API
        </Typography>
        <Typography variant="body2" style={{ marginBottom: 16, opacity: 0.7 }}>
          Use este endpoint para integrar com WordPress, N8N ou qualquer sistema externo.
        </Typography>

        {/* Colunas disponiveis */}
        <div className={classes.webhookInfo} style={{ marginBottom: 20 }}>
          <Typography className={classes.webhookTitle}>Suas colunas do Kanban</Typography>
          <Typography variant="body2" style={{ marginBottom: 12, opacity: 0.8 }}>
            Use o <code>statusId</code> para definir em qual coluna o card será criado.
          </Typography>
          {statuses.length === 0 ? (
            <Typography variant="body2" style={{ opacity: 0.5 }}>Nenhuma coluna encontrada.</Typography>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
              {statuses.sort((a, b) => a.order - b.order).map((s) => (
                <Box key={s.id} display="flex" alignItems="center" style={{ gap: 8 }}>
                  <span style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: s.color || '#999', flexShrink: 0,
                  }} />
                  <Typography variant="body2" style={{ fontWeight: 500, minWidth: 140 }}>
                    {s.name}
                  </Typography>
                  <Typography className={classes.webhookUrl} style={{
                    padding: '2px 10px', fontSize: '0.8rem', flex: 1,
                  }}>
                    "statusId": {s.id}
                  </Typography>
                  <Tooltip title="Copiar">
                    <IconButton size="small" onClick={() => handleCopy(`"statusId": ${s.id}`)}>
                      <Copy size={14} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </div>

        {/* Criar Lead */}
        <Accordion className={classes.apiDocAccordion} defaultExpanded>
          <AccordionSummary expandIcon={<ChevronDown />}>
            <Box display="flex" alignItems="center">
              <span className={`${classes.apiDocMethod} ${classes.methodPost}`}>POST</span>
              <Typography className={classes.apiDocTitle}>Criar card</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box width="100%">
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                Cria um novo card no CRM. Aceita <code>application/json</code> ou <code>multipart/form-data</code> (para enviar arquivos junto).
                Apenas <code>name</code> e obrigatorio.
              </Typography>

              <Typography variant="caption" style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Endpoint
              </Typography>
              <CodeBlock>{`POST ${baseUrl}/webhook/leads`}</CodeBlock>

              <Typography variant="caption" style={{ fontWeight: 600, marginTop: 12, marginBottom: 4, display: 'block' }}>
                Campos
              </Typography>
              <Box style={{ overflowX: 'auto', marginBottom: 12 }}>
                <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(128,128,128,0.2)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Campo</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Tipo</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>Descricao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["name *", "string", "Nome do card (obrigatório)"],
                      ["phone", "string", "Telefone (aceita whatsapp ou telefone)"],
                      ["email", "string", "E-mail"],
                      ["cpf", "string", "CPF"],
                      ["address", "string", "Endereco (aceita endereco)"],
                      ["statusId", "number", "ID da coluna (ver lista acima)"],
                      ["priority", "string", "low | normal | high | urgent"],
                      ["source", "string", "Origem (ex: wordpress, n8n, site)"],
                      ["files", "file(s)", "Arquivos anexos (multipart/form-data, ate 10)"],
                      ["customFields", "object/json", "Campos dinamicos (ver abaixo)"],
                      ["attachments", "array/json", "URLs de arquivos ja hospedados"],
                    ].map(([field, type, desc], i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: field.includes('*') ? 700 : 400 }}>{field}</td>
                        <td style={{ padding: '6px 8px', opacity: 0.7 }}>{type}</td>
                        <td style={{ padding: '6px 8px' }}>{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              <Typography variant="caption" style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Tipos de campo (customFields.fields[].type)
              </Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginBottom: 16 }}>
                {fieldTypes.map((f) => (
                  <Tooltip key={f.type} title={f.desc}>
                    <Chip label={f.type} size="small" variant="outlined" className={classes.fieldTypeChip} />
                  </Tooltip>
                ))}
              </Box>

              <Typography variant="caption" style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Exemplo com JSON (sem arquivos)
              </Typography>
              <CodeBlock>{curlJsonExample}</CodeBlock>

              <Typography variant="caption" style={{ fontWeight: 600, marginTop: 16, marginBottom: 4, display: 'block' }}>
                Exemplo com arquivos (multipart/form-data)
              </Typography>
              <CodeBlock>{curlMultipartExample}</CodeBlock>

              <Typography variant="caption" style={{ fontWeight: 600, marginTop: 16, marginBottom: 4, display: 'block' }}>
                Resposta
              </Typography>
              <CodeBlock>{`{
  "success": true,
  "leadId": "uuid-do-lead",
  "message": "Lead criado com sucesso"
}`}</CodeBlock>
            </Box>
          </AccordionDetails>
        </Accordion>
      </div>
    </>
  );
};

export default LeadSettingsContent;
